export const DASHBOARD_WIDGET_IDS = [
  'kiHub',
  'pendingFileReviews',
  'pendingAccess',
  'emails',
  'classRanking',
  'appointments',
  'timetable',
  'kpiStats',
  'activeSessions',
  'recentBlocksAndPlans',
  'nameScores',
  'quickLinks',
] as const

export type DashboardWidgetId = (typeof DASHBOARD_WIDGET_IDS)[number]

export interface DashboardWidgetDefinition {
  id: DashboardWidgetId
  title: string
}

export const DASHBOARD_WIDGET_DEFINITIONS: DashboardWidgetDefinition[] = [
  { id: 'kiHub', title: 'Monatsübersicht Assistenzsysteme' },
  { id: 'pendingFileReviews', title: 'Aktenprüfung ausstehend' },
  { id: 'pendingAccess', title: 'Zugriffsanfragen auf offizielle Akten' },
  { id: 'emails', title: 'Wichtige E-Mails' },
  { id: 'classRanking', title: 'Klassenranking' },
  { id: 'appointments', title: 'Terminbuchungen' },
  { id: 'timetable', title: 'Stundenplan' },
  { id: 'kpiStats', title: 'Kennzahlen' },
  { id: 'activeSessions', title: 'Aktive Sitzungen heute' },
  { id: 'recentBlocksAndPlans', title: 'Neueste Bausteine & Lernpläne' },
  { id: 'nameScores', title: 'Namen-lernen Highscores' },
  { id: 'quickLinks', title: 'Schnellzugriff' },
]

export const DASHBOARD_WIDGET_TITLE_BY_ID: Record<DashboardWidgetId, string> = DASHBOARD_WIDGET_DEFINITIONS.reduce(
  (acc, definition) => {
    acc[definition.id] = definition.title
    return acc
  },
  {} as Record<DashboardWidgetId, string>
)

export interface DashboardWidgetAvailability {
  kiHub: boolean
  pendingFileReviews: boolean
  pendingAccess: boolean
  emails: boolean
  classRanking: boolean
  appointments: boolean
  timetable: boolean
  kpiStats: boolean
  activeSessions: boolean
  recentBlocksAndPlans: boolean
  nameScores: boolean
  quickLinks: boolean
}

const DEFAULT_AVAILABILITY: DashboardWidgetAvailability = {
  kiHub: false,
  pendingFileReviews: true,
  pendingAccess: true,
  emails: false,
  classRanking: true,
  appointments: true,
  timetable: true,
  kpiStats: true,
  activeSessions: true,
  recentBlocksAndPlans: true,
  nameScores: true,
  quickLinks: true,
}

const toUniqueValidOrder = (widgetOrder: unknown): DashboardWidgetId[] => {
  if (!Array.isArray(widgetOrder)) return []
  const validIds = new Set<DashboardWidgetId>(DASHBOARD_WIDGET_IDS)
  const seen = new Set<DashboardWidgetId>()
  const next: DashboardWidgetId[] = []

  for (const entry of widgetOrder) {
    const id = String(entry || '') as DashboardWidgetId
    if (!validIds.has(id) || seen.has(id)) continue
    seen.add(id)
    next.push(id)
  }

  return next
}

export const getDefaultWidgetOrder = (
  availability?: Partial<DashboardWidgetAvailability>
): DashboardWidgetId[] => {
  const effectiveAvailability = { ...DEFAULT_AVAILABILITY, ...(availability || {}) }
  return DASHBOARD_WIDGET_IDS.filter((id) => effectiveAvailability[id])
}

export const mergeWidgetOrderWithDefaults = (
  widgetOrder: unknown,
  availability?: Partial<DashboardWidgetAvailability>
): DashboardWidgetId[] => {
  const defaultOrder = getDefaultWidgetOrder(availability)
  if (!Array.isArray(widgetOrder)) {
    return defaultOrder
  }

  const normalized = toUniqueValidOrder(widgetOrder)
  const defaultSet = new Set(defaultOrder)
  return normalized.filter((id) => defaultSet.has(id))
}

export const listAddableWidgets = (
  widgetOrder: DashboardWidgetId[],
  availability?: Partial<DashboardWidgetAvailability>
): DashboardWidgetDefinition[] => {
  const current = new Set(widgetOrder)
  const effectiveAvailability = { ...DEFAULT_AVAILABILITY, ...(availability || {}) }
  return DASHBOARD_WIDGET_DEFINITIONS.filter(
    (definition) => effectiveAvailability[definition.id] && !current.has(definition.id)
  )
}
